import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, X, Star, AlertTriangle, RefreshCw } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { useLocation } from "@/contexts/LocationContext";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import SocialIconRow from "@/components/SocialIcons";
import { track } from "@/lib/telemetry";
import { isFlagOn } from "@/lib/flags";
import { ApiError } from "@/lib/apiError";
import { REVIEW_PRICE_FROM } from "@/lib/reviewPricing";

// Wraps track() with the discovery_v2 flag check; identical pattern to
// LandingPage so disabling the flag silences both surfaces' telemetry
// without changing call sites.
function trackIfEnabled(name, props) {
  if (!isFlagOn("discovery_v2")) return;
  track(name, props);
}

/**
 * ExpertsPage — public directory.
 *
 * Restyled to the Clean Enterprise palette so the visitor feels they're
 * still on the same site as the landing page. Same chrome (MarketingNav +
 * Footer), same color tokens (canvas, ink, indigo, brand gold).
 *
 * Async only at launch (founder decision, 2026-10-09). A card shows the
 * written-review offer and its list price floor (lib/reviewPricing) only for
 * an expert the API says takes written reviews (`accepts_async_review`).
 * There is no live-session price, no hourly filter and no booking here.
 * Rating and session counts are shown only when they are above zero.
 */
export default function ExpertsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedCountry } = useLocation();

  const [experts,    setExperts]    = useState([]);
  // 3-state machine: 'loading' | 'error' | 'loaded'. Previously this was
  // a boolean and a `.catch()` set experts=[] which collapsed "the API
  // failed" into "the user filtered down to zero results" — misleading
  // CTA ("Clear filters") for a network/server error.
  const [fetchState, setFetchState] = useState("loading");
  const [fetchError, setFetchError] = useState(null);
  const [categories, setCategories] = useState({ industries: [], expertise: [] });

  const [filters, setFilters] = useState({
    search:    searchParams.get("search")    || "",
    industry:  searchParams.get("industry")  || "",
    expertise: searchParams.get("expertise") || "",
    aiReview:  searchParams.get("aiReview")  === "1",
  });

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchExperts();   }, [filters, selectedCountry]); // eslint-disable-line

  async function fetchCategories() {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch { /* noop */ }
  }

  async function fetchExperts() {
    setFetchState("loading");
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      if (filters.search)    params.append("search",    filters.search);
      if (filters.industry)  params.append("industry",  filters.industry);
      if (filters.expertise) params.append("expertise", filters.expertise);
      if (selectedCountry && selectedCountry !== "GLOBAL") {
        params.append("country", selectedCountry);
      }
      const { data } = await axios.get(`${API}/experts?${params.toString()}`);
      const visible = filters.aiReview ? data.filter(e => e.accepts_async_review === true) : data;
      setExperts(visible);
      setFetchState("loaded");
      trackIfEnabled("experts_results_loaded", {
        count: visible.length,
        filters: {
          has_search:    !!filters.search,
          industry:      filters.industry || null,
          expertise:     filters.expertise || null,
          ai_review:     !!filters.aiReview,
          country:       selectedCountry || null,
        },
      });
    } catch (err) {
      // Wrap with ApiError to get a friendly message + stable kind without
      // leaking raw response bodies into the UI (same discipline as
      // BR-checkout-ack). Preserve the error so the retry button can be
      // surfaced and so telemetry has the status code.
      const apiErr = ApiError.from(err);
      setExperts([]);
      setFetchError(apiErr);
      setFetchState("error");
      trackIfEnabled("experts_load_failed", {
        status: apiErr.status ?? null,
        kind:   apiErr.kind || "unknown",
      });
    }
  }

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    const next = new URLSearchParams(searchParams);
    if (value === true) next.set(key, "1");
    else if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);

    // Telemetry: search is special-cased — we send `experts_search_used`
    // with the query length only (never the text) to avoid PII / business-
    // sensitive intent landing in analytics. See telemetry.js JSDoc.
    if (key === "search") {
      trackIfEnabled("experts_search_used", {
        query_length: typeof value === "string" ? value.length : 0,
      });
    } else {
      // For filters, log the chosen value — these are bounded enums
      // (industry/expertise) or a boolean (aiReview).
      trackIfEnabled("experts_filter_changed", { key, value });
    }
  };

  const clearFilters = () => {
    setFilters({ search: "", industry: "", expertise: "", aiReview: false });
    setSearchParams({});
  };

  const activeFiltersCount = Object.entries(filters)
    .filter(([_, v]) => v && v !== "")
    .length;

  return (
    <div className="min-h-screen bg-canvas text-ink font-body">
      <MarketingNav />

      {/* Page header */}
      <section className="border-b border-slateline-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green mb-4">
            {t("experts.directory", "Expert directory")}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-ink leading-tight">
            {t("experts.title", "The people listed on Xpert.")}
          </h1>
          <p className="mt-4 text-lg text-slateline-600 max-w-2xl leading-relaxed">
            {t(
              "experts.subtitle",
              "Today, written reviews are done by Ankit Kumar, the founder.",
            )}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 z-30 bg-canvas/90 backdrop-blur border-b border-slateline-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col gap-3">
            {/* Search + AI Review toggle */}
            <div className="flex flex-col md:flex-row gap-3 md:items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slateline-400"
                        aria-hidden="true" />
                <input
                  type="text"
                  aria-label={t("experts.search", "Search by name, skill, or company")}
                  placeholder={t("experts.search", "Search by name, skill, or company…")}
                  value={filters.search}
                  onChange={(e) => updateFilter("search", e.target.value)}
                  className="w-full pl-10 pr-3 h-11 bg-white border border-slateline-200
                             focus:border-brand-green focus:ring-2 focus:ring-brand-green/20
                             rounded-md text-ink placeholder-slateline-400 outline-none transition-colors"
                />
              </div>

              <label className="inline-flex items-center gap-2 cursor-pointer
                                bg-white border border-slateline-200 hover:border-ink/30
                                rounded-md px-3 h-11 select-none">
                <input
                  type="checkbox"
                  checked={filters.aiReview}
                  onChange={(e) => updateFilter("aiReview", e.target.checked)}
                  className="h-4 w-4 rounded border-slateline-300 text-brand-green focus:ring-brand-green/30"
                />
                <span className="text-sm text-ink font-medium">
                  Takes written reviews
                </span>
              </label>
            </div>

            {/* Selects */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              <FilterSelect
                value={filters.industry}
                onChange={(v) => updateFilter("industry", v)}
                placeholder={t("experts.filters.allIndustries", "All industries")}
                options={categories.industries || []}
              />
              <FilterSelect
                value={filters.expertise}
                onChange={(v) => updateFilter("expertise", v)}
                placeholder={t("experts.filters.allExpertise", "All expertise")}
                options={categories.expertise || []}
              />
              {activeFiltersCount > 0 ? (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center justify-center gap-1.5 h-11
                             text-sm text-slateline-600 hover:text-ink"
                >
                  <X className="w-4 h-4" />
                  Clear ({activeFiltersCount})
                </button>
              ) : <div className="hidden sm:block" />}
            </div>
          </div>
        </div>
      </section>

      {/* Results — 3-state machine: loading / error / loaded (with empty
          as a sub-state of loaded). The error branch is the distinguishing
          change vs. the previous implementation, which silently fell
          through to the empty state when the API failed. */}
      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="flex items-center justify-between mb-6">
            <p
              className="text-sm text-slateline-500"
              aria-live="polite"
              aria-atomic="true"
            >
              {fetchState === "loading"
                ? "Loading experts…"
                : fetchState === "error"
                ? "Couldn't load experts."
                : `${experts.length} expert${experts.length === 1 ? "" : "s"} found`}
            </p>
            <Link
              to="/get-verified"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold
                         text-brand-green hover:text-brand-greenHover"
            >
              Just need a review? Skip browsing →
            </Link>
          </div>

          {fetchState === "loading" ? (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
              aria-busy="true"
              aria-label="Loading experts"
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white border border-slateline-200 rounded-xl h-72 animate-pulse"
                />
              ))}
            </div>
          ) : fetchState === "error" ? (
            <div
              className="text-center py-16 border border-dashed border-slateline-300 rounded-xl
                         bg-white"
              role="alert"
            >
              <AlertTriangle className="w-10 h-10 text-slateline-400 mx-auto mb-3" aria-hidden="true" />
              <p className="font-display text-xl text-ink">
                We couldn't load the expert bench.
              </p>
              <p className="mt-2 text-sm text-slateline-500 max-w-md mx-auto">
                {fetchError?.message || "Please try again in a moment."}
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={fetchExperts}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md
                             bg-brand-green hover:bg-brand-greenHover text-white text-sm font-semibold
                             shadow-sm transition-colors"
                >
                  <RefreshCw className="w-4 h-4" aria-hidden="true" /> Try again
                </button>
                <Link
                  to="/get-verified"
                  className="inline-flex items-center h-10 px-4 rounded-md
                             border border-slateline-300 hover:border-ink text-ink text-sm font-medium"
                >
                  Skip browsing — submit for review
                </Link>
              </div>
            </div>
          ) : experts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {experts.map((expert, idx) => (
                <ExpertCardLight
                  key={expert.id}
                  expert={expert}
                  onClick={() => {
                    trackIfEnabled("experts_card_clicked", {
                      expert_id: expert.id, position: idx,
                    });
                    navigate(`/experts/${expert.id}`);
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 border border-dashed border-slateline-300 rounded-xl">
              <p className="text-slateline-600 text-lg mb-2">
                No experts match those filters.
              </p>
              <p className="text-sm text-slateline-500 mb-5">
                Try loosening a filter, or skip browsing and submit your work for async review.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center h-10 px-4 rounded-md
                             border border-slateline-300 hover:border-ink text-ink text-sm font-medium"
                >
                  Clear all filters
                </button>
                <Link
                  to="/get-verified"
                  className="inline-flex items-center h-10 px-4 rounded-md
                             bg-brand-green hover:bg-brand-greenHover text-white text-sm font-semibold
                             shadow-sm transition-colors"
                >
                  Submit for review
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

// ===========================================================================
// Sub-components
// ===========================================================================

function FilterSelect({ value, onChange, placeholder, options }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="h-11 bg-white border border-slateline-200 rounded-md px-3 text-sm
                 text-ink focus:border-brand-green focus:ring-2 focus:ring-brand-green/20
                 outline-none transition-colors"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => {
        const [val, label] = Array.isArray(opt) ? opt : [opt, opt];
        return <option key={val} value={val}>{label}</option>;
      })}
    </select>
  );
}

/**
 * Light-theme expert card. Surfaces:
 *   - Headshot or monogram fallback
 *   - Name + title
 *   - Star rating + total sessions, only when above zero
 *   - "Written review from $X", only for an expert who takes written reviews
 */
function ExpertCardLight({ expert, onClick }) {
  const hasPhoto = !!expert.image_url;
  const initials = (expert.name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Async reviews are sold at fixed list prices per turnaround, the same for
  // every reviewer (backend TURNAROUND_RATES), so the card shows the floor of
  // those tiers rather than a per-expert number (Supply F12).
  const reviewPrice = REVIEW_PRICE_FROM;
  // Only an expert the API marks as taking written reviews gets the offer. The
  // three persona rows do not, so they show no price and no review CTA.
  const takesReviews = expert.accepts_async_review === true;
  const hasStats = Number(expert.rating) > 0 || Number(expert.total_sessions) > 0;

  return (
    <button
      onClick={onClick}
      className="text-left w-full bg-white rounded-xl border border-slateline-200
                 hover:border-ink/20 hover:shadow-md transition-all overflow-hidden
                 flex flex-col"
    >
      {/* Photo area */}
      <div className="aspect-[16/10] bg-slateline-100 relative overflow-hidden flex items-center justify-center">
        {hasPhoto ? (
          <img
            src={expert.image_url}
            alt={expert.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-brand-greenPale flex items-center justify-center
                          border border-brand-green/30">
            <span className="font-display text-2xl text-brand-green">{initials}</span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {expert.verified && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold
                             bg-white/95 text-ink rounded-full px-2 py-0.5
                             border border-slateline-200 shadow-sm">
              ✓ Verified
            </span>
          )}
          {takesReviews && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold
                             bg-brand-greenTint text-brand-green rounded-full px-2 py-0.5
                             border border-brand-green/30">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
              Takes written reviews
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col grow">
        <div className="font-display text-lg text-ink leading-tight">
          {expert.name}
        </div>
        <div className="text-sm text-slateline-500 line-clamp-1 mt-0.5">
          {expert.title || expert.company || "Independent expert"}
        </div>

        {hasStats && (
          <div className="mt-3 flex items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1 text-ink">
              <Star className="w-4 h-4 fill-brand-green text-brand-green" />
              <span className="font-semibold">{expert.rating?.toFixed?.(1) || "—"}</span>
            </span>
            <span className="text-slateline-500">
              {expert.total_sessions || 0} reviews
            </span>
          </div>
        )}

        {expert.bio && (
          <p className="mt-3 text-sm text-slateline-600 line-clamp-2 leading-relaxed">
            {expert.bio}
          </p>
        )}

        {/* Pricing strip: written review only, and only for someone who takes them */}
        {takesReviews && (
          <div className="mt-5 pt-4 border-t border-slateline-200 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-slateline-400 font-bold">
              Written review
            </div>
            <div className="text-ink font-semibold">From ${reviewPrice}</div>
          </div>
        )}

        {/* Social icons row — LinkedIn / GitHub / X. Stops the card click
            from bubbling so clicking an icon opens the link in a new tab
            without also routing the user to the profile page. */}
        <div
          className="mt-3 pt-3 border-t border-slateline-100"
          onClick={(e) => e.stopPropagation()}
        >
          <SocialIconRow expert={expert} size="sm" />
        </div>
      </div>
    </button>
  );
}
